import {
  type PatientStaffApiOutContract,
  type PharmacyApiOutContract,
  type VisitPatientPreferenceStaffApiOutContract,
} from 'api/generated/pwrdbyQuickMDProvidersApi.schemas'
import { type VisitPrescription } from './orders/prescriptionFulfilment'
import { isNoLongerLive, isPendingAuthorization } from './orders/prescriptionStatus'

/**
 * What the "Requested pharmacy" section shows. Async visits derive it from the visit's stored
 * `PatientPreference`; live visits that carry none fall back to the patient's own Home Delivery
 * answer (`requestedPharmacyFromPatient`).
 */
export type RequestedPharmacy =
  | { kind: 'homeDelivery' }
  | { kind: 'pharmacy'; pharmacy: PharmacyApiOutContract }
  | { kind: 'none' }

// A visit preference with no pharmacy means home delivery (`Pharmacy == null`); no preference is blank.
export const requestedPharmacyFromVisitPreference = (
  preference: VisitPatientPreferenceStaffApiOutContract | null | undefined,
): RequestedPharmacy => {
  if (!preference) return { kind: 'none' }
  return preference.Pharmacy ? { kind: 'pharmacy', pharmacy: preference.Pharmacy } : { kind: 'homeDelivery' }
}

/** Which guidance strip the Requested pharmacy card shows. */
export type HomeDeliveryGuidance = 'info' | 'warning'

/**
 * QMD-4913: nudge the provider toward Order Set when the patient asked for Home Delivery, and warn
 * once a sent script has gone to a pickup pharmacy instead.
 *
 * The warning keys off the *requested pharmacy this card shows*, not the patient-level
 * `HomeDeliveryInterest` the Notion spec names. The two only differ when a visit carries its own
 * preference (async), and there reading the patient field would put "Patient requested Home
 * Delivery" under a card that says "Pharmacy pickup at …". For a live visit without a stored
 * preference the requested pharmacy *is* `HomeDeliveryInterest`, so the rules agree.
 *
 * Scripts still pending authorization in Treat have not been sent anywhere, so they never warn; nor
 * do scripts that are no longer live (`isNoLongerLive`), or a provider who cancelled the pickup
 * script and re-prescribed from Order Set would keep the warning for the rest of the visit.
 * `IsHomeDelivery` absent (a deployment that predates the field) never warns either.
 */
export const homeDeliveryGuidance = (
  requested: RequestedPharmacy | null,
  prescriptions: readonly VisitPrescription[] | null | undefined,
): HomeDeliveryGuidance | null => {
  if (requested?.kind !== 'homeDelivery') return null
  return sentToPickup(prescriptions).length > 0 ? 'warning' : 'info'
}

/** The live, sent scripts that went to a pickup pharmacy: what the warning is about. */
export const sentToPickup = (
  prescriptions: readonly VisitPrescription[] | null | undefined,
): readonly VisitPrescription[] =>
  prescriptions?.filter((p) => p.IsHomeDelivery === false && !isPendingAuthorization(p) && !isNoLongerLive(p)) ?? []

/**
 * The patient's latest answer to the booking-flow Home Delivery touchpoint (PRJ-578), which the
 * backend stores on the patient, not on the visit. `true` = home delivery; `false` = pickup, so the
 * pharmacy on file (the touchpoint updates it when the patient picks another); `null` = never answered.
 */
export const requestedPharmacyFromPatient = (patient: PatientStaffApiOutContract | undefined): RequestedPharmacy => {
  if (patient?.HomeDeliveryInterest === true) return { kind: 'homeDelivery' }
  if (patient?.HomeDeliveryInterest === false && patient.Pharmacy)
    return { kind: 'pharmacy', pharmacy: patient.Pharmacy }
  return { kind: 'none' }
}
